import { useEffect, useState } from 'react';

import { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from 'hooks/useAuthContext';

import { ApiErrorData } from 'models/ApiResponse';
import { KitCampaign } from 'models/KitCampaign';
import {
  TiendaPerfectaDistributor,
  TiendaPerfectaDistributorsResponse,
} from 'models/TiendaPerfecta';

import { KitCampaignService } from 'services/KitCampaignService';
import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

interface UseRoleMetadataParams {
  message: MessageInstance;
}

interface UseRoleMetadataReturn {
  tableLoading: boolean;
  setTableLoading: (loading: boolean) => void;
  campaigns: KitCampaign[];
  kitIntegrationEnabled: boolean;
  tiendaPerfectaIntegrationEnabled: boolean;
  tiendaPerfectaDistributors: TiendaPerfectaDistributor[];
  tiendaPerfectaDistributorsLoading: boolean;
}

export function useRoleMetadata({
  message,
}: UseRoleMetadataParams): UseRoleMetadataReturn {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const kitIntegrationEnabled = user?.company?.kitIntegrationEnabled ?? false;

  // La compañía de Tienda Perfecta ya no se elige por rol: se hereda de la
  // compañía activa, así que el catálogo se puede pedir una sola vez acá.
  const tiendaPerfectaCompanyCode =
    user?.company?.tiendaPerfectaCompanyCode ?? null;
  const tiendaPerfectaIntegrationEnabled =
    (user?.company?.tiendaPerfectaIntegrationEnabled ?? false) &&
    !!tiendaPerfectaCompanyCode;

  const [tableLoading, setTableLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<KitCampaign[]>([]);
  const [tiendaPerfectaDistributors, setTiendaPerfectaDistributors] = useState<
    TiendaPerfectaDistributor[]
  >([]);
  const [
    tiendaPerfectaDistributorsLoading,
    setTiendaPerfectaDistributorsLoading,
  ] = useState(false);

  useEffect(() => {
    if (!kitIntegrationEnabled) return;

    const fetchCampaigns = async () => {
      const response = await KitCampaignService.getByCompany(user.company.id);

      if (response.success) {
        setCampaigns(response.data as KitCampaign[]);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t('scenes.settings.roles.scene.errors.fetch_campaigns'),
          3.5
        );
      }
    };

    fetchCampaigns();
  }, [kitIntegrationEnabled]);

  useEffect(() => {
    if (!tiendaPerfectaIntegrationEnabled || !tiendaPerfectaCompanyCode) return;

    const fetchDistributors = async () => {
      setTiendaPerfectaDistributorsLoading(true);

      // includeInactive: los dados de baja que ya estaban asignados a un rol
      // deben poder renderizarse; los que no lo estén se filtran al armar las
      // opciones del selector.
      const response = await TiendaPerfectaService.getDistributors({
        companyCode: tiendaPerfectaCompanyCode,
        includeInactive: true,
        sortBy: 'tradeName',
        sortDir: 'asc',
      });

      if (response.success) {
        setTiendaPerfectaDistributors(
          (response.data as TiendaPerfectaDistributorsResponse)?.distributors ??
            []
        );
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t('scenes.settings.roles.scene.errors.fetch_distributors'),
          3.5
        );
      }

      setTiendaPerfectaDistributorsLoading(false);
    };

    fetchDistributors();
  }, [tiendaPerfectaIntegrationEnabled, tiendaPerfectaCompanyCode]);

  return {
    tableLoading,
    setTableLoading,
    campaigns,
    kitIntegrationEnabled,
    tiendaPerfectaIntegrationEnabled,
    tiendaPerfectaDistributors,
    tiendaPerfectaDistributorsLoading,
  };
}
